import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useChat } from "../chat/ChatProvider";
import { fetchNotice, fetchNoticeSummary } from "../../controllers/noticeController";
import NoticeDetailModal from "./NoticeDetailModal";
import NoticeFormModal from "./NoticeFormModal";
import NoticeToasts from "./NoticeToasts";

// Notice-board state for the logged-in session: badge counts, a `version` that bumps whenever any notice changes
// (lists refetch on it), "new notice" toasts, and the notice / editor dialogs, which any page can open.
// Live updates arrive as `notice:changed` on the chat socket (see the server's services/noticeService.js).

const NoticeContext = createContext(null);
export const useNotices = () => useContext(NoticeContext);

const EMPTY_SUMMARY = { unreadCount: 0, pendingAckCount: 0, urgentUnreadCount: 0, canManage: false };
const MAX_TOASTS = 3;

export function NoticeProvider({ children }) {
  const { subscribe } = useChat();
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [version, setVersion] = useState(0);
  const [toasts, setToasts] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [editor, setEditor] = useState(null); // { notice } — notice is null when creating
  const openIdRef = useRef(openId);
  openIdRef.current = openId;

  const refreshSummary = useCallback(async () => {
    try {
      setSummary(await fetchNoticeSummary());
    } catch {
      /* badges just stay as they were */
    }
  }, []);

  // Something changed: badges and every mounted list refetch.
  const invalidate = useCallback(() => {
    setVersion((v) => v + 1);
    refreshSummary();
  }, [refreshSummary]);

  const dismissToast = useCallback((id) => setToasts((list) => list.filter((t) => t.id !== id)), []);

  useEffect(() => {
    refreshSummary();
  }, [refreshSummary]);

  useEffect(() => {
    const offConnect = subscribe("connect", invalidate);
    const offChanged = subscribe("notice:changed", async ({ noticeId, action } = {}) => {
      invalidate();
      if (action === "deleted" && openIdRef.current === noticeId) setOpenId(null);
      if (action !== "published") return;
      try {
        const notice = await fetchNotice(noticeId);
        // The author (and anyone who already opened it) doesn't need a toast.
        if (notice.readAt || openIdRef.current === noticeId) return;
        setToasts((list) => [notice, ...list.filter((t) => t.id !== notice.id)].slice(0, MAX_TOASTS));
      } catch {
        /* not visible to this user */
      }
    });
    return () => {
      offConnect();
      offChanged();
    };
  }, [subscribe, invalidate]);

  // Catch up after the tab was in the background (e.g. a notice expired meanwhile).
  useEffect(() => {
    const onVisible = () => document.visibilityState === "visible" && invalidate();
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [invalidate]);

  // A notice push notification was clicked while the app was open (sent by the service worker).
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return undefined;
    const onMessage = (event) => {
      if (event.data?.type === "open-notice" && event.data.noticeId) setOpenId(Number(event.data.noticeId));
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, []);

  const openNotice = useCallback((id) => {
    setOpenId(Number(id));
    setToasts((list) => list.filter((t) => t.id !== Number(id)));
  }, []);
  const closeNotice = useCallback(() => setOpenId(null), []);
  const openEditor = useCallback((notice = null) => setEditor({ notice }), []);
  const closeEditor = useCallback(() => setEditor(null), []);

  const value = useMemo(
    () => ({
      ...summary,
      version,
      refreshSummary,
      invalidate,
      openNotice,
      openEditor,
    }),
    [summary, version, refreshSummary, invalidate, openNotice, openEditor]
  );

  return (
    <NoticeContext.Provider value={value}>
      {children}
      <NoticeToasts toasts={toasts} onOpen={openNotice} onDismiss={dismissToast} />
      {openId && (
        <NoticeDetailModal
          key={openId}
          noticeId={openId}
          canManage={summary.canManage}
          version={version}
          onClose={closeNotice}
          onEdit={(notice) => {
            setOpenId(null);
            openEditor(notice);
          }}
          onChanged={invalidate}
        />
      )}
      {editor && (
        <NoticeFormModal
          notice={editor.notice}
          onClose={closeEditor}
          onSaved={(saved) => {
            closeEditor();
            invalidate();
            if (saved?.id) openNotice(saved.id);
          }}
        />
      )}
    </NoticeContext.Provider>
  );
}
